/* tslint:disable: jsx-no-lambda */
import * as React from 'react';
import { Grid, Paper, Typography, Button, CircularProgress } from '@material-ui/core';
import swal from 'sweetalert';

import { useStyles } from './styles';
import { MAX_ROWS, POLL_MAX_MS, QUOTE_TYPE, emptyRow } from './resources';
import { LICENSE_PLATE_VALIDATION } from 'constants/index';
import CommonFields from 'components/BulkQuotation/CommonFields/CommonFields';
import RowsTable, { TRowErrors } from 'components/BulkQuotation/RowsTable/RowsTable';
import SubmitSummary from 'components/BulkQuotation/SubmitSummary/SubmitSummary';
import BatchResults from 'components/BulkQuotation/BatchResults/BatchResults';
import { createBulkQuotation } from 'services/BulkQuotationService';
import { useInsurers } from 'hooks/useInsurers';

type TOption = { value: string; label: string };
type TStep = 'form' | 'summary' | 'results';

const LS_KEY = 'bulk-quote:last';
const DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;

const readLastBatch = (): IBulkQuoteLastBatch | null => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IBulkQuoteLastBatch;
    if (!parsed.batchId) return null;
    if (Date.now() - parsed.createdAt > POLL_MAX_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const BulkQuotation: React.FC = () => {
  const classes = useStyles();
  const { filteredInsurers } = useInsurers();

  const [step, setStep] = React.useState<TStep>('form');
  const [rows, setRows] = React.useState<IBulkQuoteRow[]>([emptyRow()]);
  const [insurers, setInsurers] = React.useState<TOption[]>([]);
  const [oneroso, setOneroso] = React.useState<string>('no');
  const [rowErrors, setRowErrors] = React.useState<TRowErrors>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [createResult, setCreateResult] = React.useState<IBulkQuoteCreateResponse | null>(null);
  const [batchId, setBatchId] = React.useState<string>('');
  const [resumeBatch, setResumeBatch] = React.useState<IBulkQuoteLastBatch | null>(null);

  React.useEffect(() => {
    document.title = 'Cotización masiva';
    setResumeBatch(readLastBatch());
  }, []);

  React.useEffect(() => {
    if (insurers.length === 0 && filteredInsurers.length > 0) {
      setInsurers(filteredInsurers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInsurers]);

  const resetForm = () => {
    setRows([emptyRow()]);
    setRowErrors([]);
    setCreateResult(null);
    setBatchId('');
    setStep('form');
  };

  const validateRows = (): TRowErrors | null => {
    const errs: TRowErrors = rows.map((r) => {
      const e: Partial<Record<keyof IBulkQuoteRow, string>> = {};
      if (!r.firstName.trim()) e.firstName = 'Requerido';
      if (!r.lastName.trim()) e.lastName = 'Requerido';
      if (!r.nDocument.trim()) e.nDocument = 'Requerido';
      else if (!/^[1-9]\d{3,14}$/.test(r.nDocument.trim())) e.nDocument = 'Número inválido';
      if (!r.fBorn.trim()) e.fBorn = 'Requerido';
      else if (!DATE_RE.test(r.fBorn.trim())) e.fBorn = 'DD/MM/AAAA';
      if (!r.licenseVehicle.trim()) e.licenseVehicle = 'Requerido';
      else if (!LICENSE_PLATE_VALIDATION.test(r.licenseVehicle.trim()))
        e.licenseVehicle = 'Placa inválida';
      if (!r.city || !r.city.value) e.city = 'Requerido';
      return e;
    });
    const hasError = errs.some((e) => Object.keys(e).length > 0);
    return hasError ? errs : null;
  };

  const onSubmit = async () => {
    if (insurers.length === 0) {
      swal('Faltan datos', 'Seleccioná al menos una aseguradora.', 'warning');
      return;
    }
    const errs = validateRows();
    if (errs) {
      setRowErrors(errs);
      swal('Revisá las filas', 'Hay campos incompletos o con errores.', 'warning');
      return;
    }
    setRowErrors([]);

    const body: IBulkQuoteBody = {
      clientRequestId:
        typeof crypto !== 'undefined' && (crypto as any).randomUUID
          ? (crypto as any).randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      defaults: {
        quoteType: QUOTE_TYPE,
        selectedInsurers: insurers.map((i) => i.value),
        oneroso,
      },
      items: rows.map((r) => ({
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim(),
        documentType: r.documentType,
        nDocument: Number(r.nDocument),
        gender: r.gender,
        fBorn: r.fBorn.trim(),
        licenseVehicle: r.licenseVehicle.trim(),
        cityCirculation: r.city ? [r.city.value] : [],
      })),
    };

    setSubmitting(true);
    try {
      const res = await createBulkQuotation(body);
      setCreateResult(res);
      if (res.batchId && res.accepted && res.accepted.length > 0) {
        const last: IBulkQuoteLastBatch = {
          batchId: res.batchId,
          createdAt: Date.now(),
          plates: rows.map((r) => r.licenseVehicle.trim()),
        };
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(last));
        } catch {
          /* localStorage no disponible */
        }
        setBatchId(res.batchId);
      }
      setStep('summary');
    } catch (error) {
      const err = error as any;
      const statusCode = err.response?.status;
      const data = err.response?.data;
      if (statusCode === 403) {
        swal('Sin permiso', 'Solo los administradores pueden crear cotizaciones masivas.', 'error');
      } else if (data && Array.isArray(data.rejected)) {
        setCreateResult(data as IBulkQuoteCreateResponse);
        setStep('summary');
      } else {
        swal(
          'Error',
          data?.message || 'No se pudo crear el lote. Revisá la información e intentá de nuevo.',
          'error'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Resume banner ----
  if (step === 'form' && resumeBatch && !batchId) {
    return (
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Typography variant="h5" className={classes.title}>
            Cotización masiva
          </Typography>
          <Typography>
            Hay un lote reciente en seguimiento (<code>{resumeBatch.batchId}</code>).
          </Typography>
          <div className={classes.actions}>
            <Button
              onClick={() => {
                try {
                  localStorage.removeItem(LS_KEY);
                } catch {
                  /* noop */
                }
                setResumeBatch(null);
              }}>
              Empezar uno nuevo
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setBatchId(resumeBatch.batchId);
                setResumeBatch(null);
                setStep('results');
              }}>
              Ver ese lote
            </Button>
          </div>
        </Paper>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <div className={classes.head}>
          <Typography variant="h5" className={classes.title}>
            Cotización masiva
          </Typography>
        </div>

        {step === 'form' && (
          <>
            <section className={classes.section}>
              <CommonFields
                insurers={insurers}
                setInsurers={setInsurers}
                oneroso={oneroso}
                setOneroso={setOneroso}
              />
            </section>

            <div className={classes.rowsHeader}>
              <span className={classes.sectionLabel}>Cotizaciones</span>
              <span className={classes.counter}>
                {rows.length}/{MAX_ROWS}
              </span>
            </div>
            <RowsTable rows={rows} onChange={setRows} rowErrors={rowErrors} />
            <Typography variant="caption" className={classes.hint}>
              Solo para vehículos usados.
            </Typography>

            <div className={classes.actions}>
              {submitting ? (
                <CircularProgress />
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.button}
                  onClick={onSubmit}>
                  Cotizar lote
                </Button>
              )}
            </div>
          </>
        )}

        {step === 'summary' && createResult && (
          <SubmitSummary
            result={createResult}
            rows={rows}
            onGoResults={() => setStep('results')}
            onNewBatch={resetForm}
          />
        )}

        {step === 'results' && batchId && <BatchResults batchId={batchId} onNewBatch={resetForm} />}
      </Paper>
    </Grid>
  );
};

export default BulkQuotation;
